// {CC 03/12/2026} Website Demo V2 - Aura visualizer with bidirectional WebSocket audio
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff } from 'lucide-react';
import VoiceVisualizer from './VoiceVisualizer';

const WEBSOCKET_URL = 'wss://smartoperator-prod-main-api-103200896563.europe-west4.run.app/website';
const RECEIVE_SAMPLE_RATE = 24000;
const SEND_SAMPLE_RATE = 16000;
const SPEAKING_TIMEOUT_MS = 300;

const WebsiteDemoViewerV2 = () => {
  const [sessionActive, setSessionActive] = useState(false);
  const [status, setStatus] = useState('disconnected');
  const [visualizerState, setVisualizerState] = useState('idle');

  const visualizerRef = useRef(null);
  const websocketRef = useRef(null);
  const micStreamRef = useRef(null);
  const sendAudioContextRef = useRef(null);
  const receiveAudioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const audioQueueRef = useRef([]);
  const nextPlayTimeRef = useRef(0);
  const isPlayingRef = useRef(false);
  const activeSourcesRef = useRef([]);
  const speakingTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const computeRmsLevel = useCallback((arrayBuffer) => {
    const pcmData = new Int16Array(arrayBuffer);
    let sumSquares = 0;
    for (let i = 0; i < pcmData.length; i++) {
      const normalized = pcmData[i] / 32768.0;
      sumSquares += normalized * normalized;
    }
    const rms = Math.sqrt(sumSquares / pcmData.length);
    return Math.min(1, rms * 4);
  }, []);

  const processAudioQueue = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }
    const arrayBuffer = audioQueueRef.current.shift();
    const pcmI16 = new Int16Array(arrayBuffer);
    const pcmF32 = new Float32Array(pcmI16.length);
    for (let i = 0; i < pcmF32.length; i++) {
      pcmF32[i] = pcmI16[i] / 32768.0;
    }
    const audioBuffer = receiveAudioContextRef.current.createBuffer(1, pcmF32.length, RECEIVE_SAMPLE_RATE);
    audioBuffer.copyToChannel(pcmF32, 0);
    const source = receiveAudioContextRef.current.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(receiveAudioContextRef.current.destination);
    activeSourcesRef.current.push(source);
    const currentTime = receiveAudioContextRef.current.currentTime;
    const scheduledTime = Math.max(currentTime + 0.010, nextPlayTimeRef.current);
    source.start(scheduledTime);
    nextPlayTimeRef.current = scheduledTime + audioBuffer.duration;
    source.onended = () => {
      try { source.disconnect(); } catch (_) {}
      const idx = activeSourcesRef.current.indexOf(source);
      if (idx !== -1) activeSourcesRef.current.splice(idx, 1);
    };
    if (audioQueueRef.current.length > 0) {
      setTimeout(() => processAudioQueue(), 0);
    } else {
      isPlayingRef.current = false;
    }
  }, []);

  const setupMicAndWorklet = useCallback(async () => {
    try {
      sendAudioContextRef.current = new AudioContext({ sampleRate: SEND_SAMPLE_RATE });
      if (sendAudioContextRef.current.state === 'suspended') {
        await sendAudioContextRef.current.resume();
      }
      const source = sendAudioContextRef.current.createMediaStreamSource(micStreamRef.current);
      await sendAudioContextRef.current.audioWorklet.addModule(process.env.PUBLIC_URL + '/pcm16-processor.js');
      workletNodeRef.current = new AudioWorkletNode(sendAudioContextRef.current, 'pcm16-processor');
      workletNodeRef.current.port.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer && websocketRef.current?.readyState === WebSocket.OPEN) {
          const audioData = new Uint8Array(event.data);
          const header = new Uint8Array([65]);
          const combined = new Uint8Array(header.length + audioData.length);
          combined.set(header);
          combined.set(audioData, header.length);
          websocketRef.current.send(combined.buffer);
        }
      };
      source.connect(workletNodeRef.current);
      const zeroGain = sendAudioContextRef.current.createGain();
      zeroGain.gain.value = 0;
      workletNodeRef.current.connect(zeroGain);
      zeroGain.connect(sendAudioContextRef.current.destination);
    } catch (error) {
      console.error('[DEMO_V2] Mic setup error:', error);
      setStatus('error');
    }
  }, []);

  const stopSession = useCallback(() => {
    if (speakingTimeoutRef.current) {
      clearTimeout(speakingTimeoutRef.current);
      speakingTimeoutRef.current = null;
    }
    activeSourcesRef.current.forEach((src) => {
      try { src.stop(); } catch (_) {}
      try { src.disconnect(); } catch (_) {}
    });
    activeSourcesRef.current = [];
    if (websocketRef.current) {
      if (websocketRef.current.readyState === WebSocket.OPEN) {
        websocketRef.current.close();
      }
      websocketRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (sendAudioContextRef.current) {
      sendAudioContextRef.current.close();
      sendAudioContextRef.current = null;
    }
    if (receiveAudioContextRef.current) {
      receiveAudioContextRef.current.close();
      receiveAudioContextRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    if (visualizerRef.current) {
      visualizerRef.current.disconnectAnalyser();
    }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    if (isMountedRef.current) {
      setSessionActive(false);
      setStatus('disconnected');
      setVisualizerState('idle');
    }
  }, []);

  const startSession = useCallback(async () => {
    if (sessionActive) return;
    try {
      setStatus('requesting_permissions');
      micStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      console.log('[DEMO_V2] Microphone granted');
      setStatus('connecting');

      // Connect an analyser to the mic stream for the visualizer
      if (visualizerRef.current) {
        visualizerRef.current.connectAnalyserToStream(micStreamRef.current);
      }

      receiveAudioContextRef.current = new AudioContext({
        sampleRate: RECEIVE_SAMPLE_RATE,
        latencyHint: 'interactive',
      });
      if (receiveAudioContextRef.current.state === 'suspended') {
        await receiveAudioContextRef.current.resume();
      }
      nextPlayTimeRef.current = receiveAudioContextRef.current.currentTime;
      audioQueueRef.current = [];

      websocketRef.current = new WebSocket(WEBSOCKET_URL);
      websocketRef.current.binaryType = 'arraybuffer';

      websocketRef.current.onopen = async () => {
        setStatus('connected');
        await setupMicAndWorklet();
        setSessionActive(true);
        setVisualizerState('listening');
      };

      websocketRef.current.onmessage = (event) => {
        if (typeof event.data === 'string') {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'generation_interrupted') {
              activeSourcesRef.current.forEach((src) => {
                try { src.stop(); } catch (_) {}
                try { src.disconnect(); } catch (_) {}
              });
              activeSourcesRef.current = [];
              audioQueueRef.current = [];
              isPlayingRef.current = false;
              nextPlayTimeRef.current = receiveAudioContextRef.current.currentTime + 0.010;
              setVisualizerState('listening');
              if (visualizerRef.current) {
                visualizerRef.current.setAudioLevel(0);
              }
            }
          } catch (e) {}
        } else if (event.data instanceof ArrayBuffer) {
          // Switch to speaking state and feed audio level
          setVisualizerState('speaking');
          const level = computeRmsLevel(event.data);
          if (visualizerRef.current) {
            visualizerRef.current.setAudioLevel(level);
          }

          // Reset speaking timeout
          if (speakingTimeoutRef.current) {
            clearTimeout(speakingTimeoutRef.current);
          }
          speakingTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              setVisualizerState('listening');
              if (visualizerRef.current) {
                visualizerRef.current.setAudioLevel(0);
              }
            }
          }, SPEAKING_TIMEOUT_MS);

          audioQueueRef.current.push(event.data);
          if (!isPlayingRef.current) {
            isPlayingRef.current = true;
            processAudioQueue();
          }
        }
      };

      websocketRef.current.onclose = () => {
        console.log('[DEMO_V2] WebSocket closed');
        stopSession();
      };

      websocketRef.current.onerror = (error) => {
        console.error('[DEMO_V2] WebSocket error:', error);
        setStatus('error');
        stopSession();
      };
    } catch (error) {
      console.error('[DEMO_V2] Error starting session:', error);
      if (error.name === 'NotAllowedError') {
        setStatus('permission_denied');
      } else {
        setStatus('error');
      }
      stopSession();
    }
  }, [sessionActive, setupMicAndWorklet, stopSession, processAudioQueue, computeRmsLevel]);

  const handleToggle = useCallback(() => {
    if (status === 'connecting' || status === 'requesting_permissions') return;
    if (sessionActive) {
      stopSession();
    } else {
      startSession();
    }
  }, [sessionActive, status, startSession, stopSession]);

  const isLoading = status === 'connecting' || status === 'requesting_permissions';

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <VoiceVisualizer
        ref={visualizerRef}
        state={visualizerState}
        fillScreen
      />

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
        }}
      >
        <button
          onClick={handleToggle}
          disabled={isLoading}
          style={{
            width: 82,
            height: 82,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            backdropFilter: 'blur(4px)',
            cursor: isLoading ? 'wait' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
            transition: 'all 0.2s ease',
            outline: 'none',
            backgroundColor: sessionActive ? '#656e55' : 'rgba(184, 200, 176, 0.35)',
            borderColor: sessionActive ? '#555e48' : 'rgba(200, 216, 192, 0.5)',
            color: sessionActive ? '#f5f3ef' : '#7a9470',
          }}
        >
          {sessionActive ? (
            <MicOff size={32} strokeWidth={1} />
          ) : (
            <Mic size={32} strokeWidth={1} />
          )}
        </button>
      </div>

      {status === 'permission_denied' && (
        <div
          style={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 20,
            backgroundColor: 'rgba(255, 243, 205, 0.95)',
            color: '#856404',
            padding: '12px 24px',
            borderRadius: 8,
            fontSize: 14,
            fontFamily: "'Open Sans', sans-serif",
            maxWidth: '90%',
            textAlign: 'center',
            backdropFilter: 'blur(8px)',
          }}
        >
          Please enable microphone access in your browser settings and refresh the page.
        </div>
      )}

      {isLoading && (
        <div
          style={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 20,
            color: '#5a6650',
            fontSize: 14,
            fontFamily: "'Open Sans', sans-serif",
          }}
        >
          {status === 'requesting_permissions' ? 'Requesting microphone access...' : 'Connecting...'}
        </div>
      )}
    </div>
  );
};

export default WebsiteDemoViewerV2;
